import { AxiosRequestConfig } from "axios";
import CommonService from "../Common";

export interface ListAnalysesParams {
  name?: string | any;
  status?: "draft" | "complete" | "archived";
  sortBy?: "createdAt" | "updatedAt" | "name";
  sortOrder?: "asc" | "desc";
  limit?: number;
  page?: number;
  dateFrom?: string | any;
  dateTo?: string | any;
}

export interface StartGenerationParams {
  userId: string | any;
  modelAPIKey: string | any;
  socketId?: string | any;
  priority?: number;
  customInstructions?: string | any;
  icpId: string | any;
  competitorData: string | any;
  modelSelection?: "openai" | "anthropic" | "deepseek" | "gemini";
}

class CompetitiveAnalysisService {
  private readonly pathPrefix: string | any;
  private readonly commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/projects";
    this.commonService = new CommonService();
  }

  // GET /api/v1/projects/:projectId/competitive-analysis
  async listAnalyses(
    projectId: string | any,
    userId: string | any,
    params?: ListAnalysesParams
  ) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/competitive-analysis`,
      params: {
        projectId,
        userId,
        ...params,
      },
      withCredentials: true,
    };
    const response = await this.commonService.fetch(config);

    // Parse any stringified JSON in the analysis field
    const analyses = response.data.analyses.map((analysis: any) => ({
      ...analysis,
      analysis:
        typeof analysis.analysis === "string"
          ? JSON.parse(analysis.analysis)
          : analysis.analysis,
    }));

    return {
      ...response.data,
      analyses,
    };
  }

  // GET /api/v1/projects/:projectId/competitive-analysis/:analysisId
  async getAnalysis(projectId: string | any, analysisId: string | any) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/competitive-analysis/${analysisId}`,
      withCredentials: true,
    };
    const analysis = await this.commonService.fetch(config);

    return {
      ...analysis,
      analysis:
        typeof analysis.analysis === "string"
          ? JSON.parse(analysis.analysis)
          : analysis.analysis,
    };
  }

  // DELETE /api/v1/projects/:projectId/competitive-analysis/:analysisId
  async deleteAnalysis(projectId: string | any, analysisId: string | any) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${projectId}/competitive-analysis/${analysisId}`,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/competitive-analysis/generate
  async startAnalysisGeneration(
    projectId: string | any,
    params: StartGenerationParams
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/competitive-analysis/generate`,
      data: params,
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/competitive-analysis/:analysisId/qa
  async answerQuestionOnDocument(
    userId: string | any,
    projectId: string | any,
    analysisId: string | any,
    question: string,
    modelAPIKey: string | any
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/competitive-analysis/${analysisId}/qa`,
      data: { question, userId, modelAPIKey },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }
}

export default CompetitiveAnalysisService;
